import { useState, useEffect } from "react";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";
import Modal from "@/components/base/Modal";
import { type UserProfile } from "@/mocks/data";
import { userService } from "@/services/api";

interface ColegioUser {
  id: string;
  nombre: string;
  email: string;
  rol: string;
  activo: boolean;
  ultimaActividad: string;
}

const roleLabels: Record<string, string> = {
  admin: "Administrador",
  inspector: "Inspector General",
  profesor: "Profesor",
  orientador: "Orientador",
  directivo: "Directivo",
  psicologo: "Psicólogo",
};

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

export default function AdminUsuariosPage() {
  const [user] = useState<UserProfile | null>(getStoredUser);
  const [users, setUsers] = useState<ColegioUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ nombre: "", email: "", rol: "profesor" });
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await userService.list();
      if (Array.isArray(data)) {
        setUsers(data.map((u: any) => ({
          id: u.id,
          nombre: u.nombre,
          email: u.email,
          rol: u.rol,
          activo: !!u.activo,
          ultimaActividad: u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleString("es-CL") : "Nunca"
        })));
      } else {
        throw new Error("Invalid list format received from API");
      }
    } catch (err: any) {
      console.error("Error loading users:", err);
      setError("Error al cargar usuarios desde base de datos Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadUsers();
    }
  }, [user]);

  if (!user) return null;

  const filtered = users.filter(
    (u) =>
      u.nombre.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      roleLabels[u.rol]?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSaveUser = async () => {
    if (!newUser.nombre || !newUser.email) return;

    try {
      if (editingId) {
        await userService.update(editingId, {
          nombre: newUser.nombre,
          rol: newUser.rol
        });
      } else {
        await userService.create({
          nombre: newUser.nombre,
          email: newUser.email,
          password: "EzerPassword2026!", // default password for new users
          rol: newUser.rol
        });
      }
      setNewUser({ nombre: "", email: "", rol: "profesor" });
      setEditingId(null);
      setShowNewUser(false);
      await loadUsers();
    } catch (err: any) {
      alert("Error al guardar usuario en base de datos: " + (err.response?.data?.detail || err.message));
    }
  };

  const toggleUserStatus = async (id: string) => {
    try {
      await userService.toggleStatus(id);
      await loadUsers();
    } catch (err: any) {
      alert("Error al cambiar estado en base de datos: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setNewUser({ nombre: "", email: "", rol: "profesor" });
    setShowNewUser(true);
  };

  const handleOpenEdit = (u: ColegioUser) => {
    setEditingId(u.id);
    setNewUser({ nombre: u.nombre, email: u.email, rol: u.rol });
    setShowNewUser(true);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Administración de Usuarios</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Gestión de cuentas institucionales, control de acceso y asignación de roles del personal.</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-850 text-xs rounded-xl font-medium">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <Input
          placeholder="Buscar por nombre, email o rol..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<i className="ri-search-line" />}
          className="max-w-sm"
          fullWidth={false}
        />
        <Button onClick={handleOpenAdd} className="cursor-pointer bg-primary-500 text-white hover:bg-primary-600">
          <i className="ri-user-add-line mr-1.5" /> Agregar Usuario
        </Button>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50/50">
                  <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs">Usuario</th>
                  <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs">Email</th>
                  <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs">Rol</th>
                  <th className="text-center py-3 px-4 text-foreground-500 font-medium text-xs">Estado</th>
                  <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs hidden md:table-cell">Última actividad</th>
                  <th className="text-right py-3 px-4 text-foreground-500 font-medium text-xs">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-primary-700">{u.nombre.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-foreground-900 whitespace-nowrap">{u.nombre}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-foreground-600 max-w-[180px] truncate">{u.email}</td>
                    <td className="py-3 px-4">
                      <Badge variant="primary" size="sm">{roleLabels[u.rol] || u.rol}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => toggleUserStatus(u.id)}
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full cursor-pointer transition-colors ${
                          u.activo
                            ? "bg-accent-100 text-accent-800 hover:bg-accent-200"
                            : "bg-background-200 text-foreground-500 hover:bg-background-300"
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${u.activo ? "bg-accent-500" : "bg-foreground-400"}`} />
                        {u.activo ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-xs text-foreground-500 hidden md:table-cell whitespace-nowrap">{u.ultimaActividad}</td>
                    <td className="py-3 px-4 text-right">
                      <button 
                        onClick={() => handleOpenEdit(u)}
                        className="text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer p-1"
                      >
                        <i className="ri-edit-line text-lg" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-foreground-400">
                      No se encontraron usuarios en la base de datos de este establecimiento.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Modal
        open={showNewUser}
        onClose={() => setShowNewUser(false)}
        title={editingId ? "Editar Usuario" : "Crear nuevo usuario"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowNewUser(false)} className="cursor-pointer">Cancelar</Button>
            <Button onClick={handleSaveUser} className="cursor-pointer">
              {editingId ? "Guardar Cambios" : "Crear usuario"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Nombre completo"
            placeholder="Nombre del funcionario"
            value={newUser.nombre}
            onChange={(e) => setNewUser({ ...newUser, nombre: e.target.value })}
          />
          <Input
            label="Email institucional"
            type="email"
            placeholder="email@colegio.cl"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            disabled={!!editingId}
          />
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Rol</label>
            <select
              value={newUser.rol}
              onChange={(e) => setNewUser({ ...newUser, rol: e.target.value })}
              className="w-full text-sm rounded-lg border-2 border-background-300 bg-background-50 text-foreground-900 px-3.5 py-2.5 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none cursor-pointer"
            >
              {Object.entries(roleLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}